import { firebaseApp } from './firebase';

/**
 * What the free-trial form and the forgot-password form share: the Firebase phone-OTP plumbing,
 * the messages for its errors, and a fetch that gives up — the server's work (making an
 * organisation, resetting a password) runs inside the request, and a proxy that cuts a slow one
 * must not leave the person staring at a spinner.
 */

export const FIREBASE_ERRORS: Record<string, string> = {
  'auth/invalid-phone-number': 'Enter a 10-digit Indian mobile number.',
  'auth/too-many-requests': 'Too many attempts from this device. Please try again later.',
  'auth/invalid-verification-code': 'That code is not right. Check the SMS and try again.',
  'auth/code-expired': 'That code has expired. Send a new one.',
  'auth/captcha-check-failed': 'The security check failed. Reload the page and try again.',
  'auth/quota-exceeded': 'We cannot send codes right now. Please try again later.',
};

/** Firebase Auth is ~100 KB the rest of the site never needs, so it loads with the first code. */
export const loadAuth = () => import('firebase/auth');

/** Signs the phone back out of Firebase: the ID token was all the server needed from it. */
export async function signOutPhone() {
  const app = firebaseApp();
  if (!app) return;
  const { getAuth, signOut } = await loadAuth();
  await signOut(getAuth(app)).catch(() => undefined);
}

export function firebaseMessage(error: unknown, fallback: string): string {
  const code = (error as { code?: string })?.code;
  return (code && FIREBASE_ERRORS[code]) || fallback;
}

/** A control's value whether or not its fieldset is disabled (`FormData` skips disabled ones). */
export function fieldValue(element: HTMLFormElement, name: string): string {
  const control = element.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
  return control?.value ?? '';
}

export const formatDate = (epochMs: number) =>
  new Date(epochMs).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

export class RequestTimeoutError extends Error {
  constructor() {
    super('The request took too long.');
    this.name = 'RequestTimeoutError';
  }
}

/** `fetch` that aborts after `timeoutMs`, rejecting with `RequestTimeoutError`. */
export async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (error) {
    if ((error as { name?: string })?.name === 'AbortError') throw new RequestTimeoutError();
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export const postJson = (url: string, body: unknown, timeoutMs: number) =>
  fetchWithTimeout(
    url,
    { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) },
    timeoutMs,
  );
